import { createSlice } from '@reduxjs/toolkit';
import { xor } from 'lib';
import {
  handleResetFilter,
  handleSetAdvertFilterValues,
  handleSetSelectedCategories,
  handleSetSelectedContentBasedValues,
  handleSetSelectedFilterIds,
  handleSetSelectedFilterValue,
  handleSetSelectedItems,
  handleSetSelectedSentimentValues,
} from 'store/helpers/filterHelpers';
import { autoBulletinInitialState } from './initialState/autoBulletin.initialState';
import {
  toggleActivationAutoBulletin,
  deleteAutoBulletin,
  fetchAutoBulletin,
  fetchAutoBulletins,
  addAutoBulletin,
  shareHashAutoBulletin,
  updateAutoBulletin,
  fetchAutoBulletinArchives,
  fetchAutoBulletinContents,
  fetchAutoBulletinByHash,
  deleteAutoBulletinContent,
  pinAutoBulletinContents,
  fetchWebBulletinContents,
  fetchWebBulletinByHash,
  fetchWebBulletinPlatforms,
  uploadBulletinImage,
} from './autoBulletin.actions';
import { prepareAutoBulletinData } from './autoBulletin.helpers';

export const autoBulletinSlice = createSlice({
  name: 'autoBulletinSlice',
  initialState: autoBulletinInitialState,
  reducers: {
    setCurrentBulletinInitialValues: (state) => {
      state.currentBulletin = autoBulletinInitialState.currentBulletin;
      state.selectedPreviewTab = autoBulletinInitialState.selectedPreviewTab;
    },
    toggleCurrentBulletinModal: (state, action) => {
      if (action?.payload?.id && !state.currentBulletin.open) {
        const selectedBulletin = state.autoBulletins.data.find(
          (item) => item.id === action.payload.id
        );

        state.currentBulletin = {
          ...autoBulletinInitialState.currentBulletin,
          ...prepareAutoBulletinData(selectedBulletin, true),
        };
      } else if (!state.currentBulletin.open) {
        state.currentBulletin = {
          ...autoBulletinInitialState.currentBulletin,
          open: true,
        };
      } else {
        state.currentBulletin.open = false;
      }
    },
    changeCurrentBulletinValue: (state, action) => {
      const { key, value } = action.payload;
      state.currentBulletin[key] = value;
    },
    setSelectedSort: (state, action) => {
      const { platform, selectedSort } = action.payload;
      state.currentBulletin.selectedSort[platform] = selectedSort;
    },
    setSelectedContents: (state, action) => {
      const { id, checked, platform } = action.payload;
      if (!id) {
        state.selectedContents = checked
          ? state.contents[platform].data.map((content) => content.id)
          : [];
        return;
      }
      state.selectedContents = xor(state.selectedContents, [id]);
    },
    setSelectedArchive: (state, action) => {
      state.selectedArchive = action.payload;
    },
    setSelectedContent: (state, action) => {
      state.selectedContent = action.payload;
    },
    toggleContentDetails: (state, action) => {
      state.isDetailsOpen = action.payload;
    },
    undoRemovedContent: (state, action) => {
      const { contents, platform } = action.payload;

      state.contents[platform].total += contents.length;

      contents.forEach((content) => {
        state.contents[platform].data.splice(
          state.contents[platform].toRemoveIndexes[content.id],
          0,
          content
        );
      });
    },
    removeContent: (state, action) => {
      const { ids, platform } = action.payload;

      const removeIndexes = {};

      state.contents[platform].total -= ids.length;

      state.contents[platform].data = state.contents[platform].data.filter(
        (content, index) => {
          if (ids.includes(content.id)) {
            removeIndexes[content.id] = index;
          }
          return !ids.includes(content.id);
        }
      );

      state.contents[platform].toRemoveIndexes = removeIndexes;
    },
    setAutoBulletinParamValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setAdvertFilterValues: (state, action) => {
      handleSetAdvertFilterValues(state, action);
    },
    setSelectedContentBasedValues: (state, action) => {
      handleSetSelectedContentBasedValues(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(
        state,
        action,
        autoBulletinInitialState.filters
      );
    },
    resetAllFilters: (state) => {
      state.filters = autoBulletinInitialState.filters;
    },
    toggleAutoBulletinFilterActivateSwitch: (state, action) => {
      const { componentName } = action.payload;
      const filter = state.filters[state.selectedPreviewTab][componentName];
      filter.isDisabled = !filter.isDisabled;
    },
    setSelectedCategories: (state, action) => {
      handleSetSelectedCategories(state, action);
    },
    resetFilter: (state, action) => {
      handleResetFilter(state, action, autoBulletinInitialState.filters);
    },
    setSelectedItems: (state, action) => {
      handleSetSelectedItems(state, action);
    },
    setSelectedSentimentValues: (state, action) => {
      handleSetSelectedSentimentValues(state, action);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAutoBulletin.fulfilled, (state, action) => {
        const { advancedFilters, ...rest } = action.payload;

        state.currentBulletin = {
          ...autoBulletinInitialState.currentBulletin,
          ...prepareAutoBulletinData(rest),
        };

        state.filters = advancedFilters;

        state.autoBulletins.loading = false;
        state.autoBulletins.error = null;
      })
      .addCase(fetchAutoBulletin.pending, (state) => {
        state.autoBulletins.loading = true;
        state.autoBulletins.error = null;
      })
      .addCase(fetchAutoBulletin.rejected, (state, action) => {
        state.autoBulletins.loading = false;
        state.autoBulletins.error = action.payload;
      })
      .addCase(fetchAutoBulletins.fulfilled, (state, action) => {
        state.autoBulletins.data = action.payload;
        state.autoBulletins.loading = false;
        state.autoBulletins.error = null;
      })
      .addCase(fetchAutoBulletins.pending, (state) => {
        state.autoBulletins.loading = true;
        state.autoBulletins.error = null;
      })
      .addCase(fetchAutoBulletins.rejected, (state, action) => {
        state.autoBulletins.loading = false;
        state.autoBulletins.error = action.payload;
      })
      .addCase(addAutoBulletin.fulfilled, (state, action) => {
        state.currentBulletin.open =
          autoBulletinInitialState.currentBulletin.open;
        state.autoBulletins.data.push(action.payload);
        state.saveAutoBulletinRequest.loading = false;
        state.saveAutoBulletinRequest.error = null;
      })
      .addCase(addAutoBulletin.pending, (state) => {
        state.saveAutoBulletinRequest.loading = true;
        state.saveAutoBulletinRequest.error = null;
      })
      .addCase(addAutoBulletin.rejected, (state, action) => {
        state.saveAutoBulletinRequest.loading = false;
        state.saveAutoBulletinRequest.error = action.payload;
      })
      .addCase(updateAutoBulletin.fulfilled, (state, action) => {
        const data = action?.payload ?? {};
        state.currentBulletin.open =
          autoBulletinInitialState.currentBulletin.open;
        state.autoBulletins.data = state.autoBulletins.data?.map((item) => {
          if (item.id === data?.id) return data;
          return item;
        });
        state.saveAutoBulletinRequest.loading = false;
        state.saveAutoBulletinRequest.error = null;
      })
      .addCase(updateAutoBulletin.pending, (state) => {
        state.saveAutoBulletinRequest.loading = true;
        state.saveAutoBulletinRequest.error = null;
      })
      .addCase(updateAutoBulletin.rejected, (state, action) => {
        state.saveAutoBulletinRequest.loading = false;
        state.saveAutoBulletinRequest.error = action.payload;
      })
      .addCase(deleteAutoBulletin.fulfilled, (state, action) => {
        state.autoBulletins.data = state.autoBulletins.data?.filter(
          (item) => item.id !== action.payload
        );
        state.deleteAutoBulletinRequest.loading = false;
        state.deleteAutoBulletinRequest.error = null;
      })
      .addCase(deleteAutoBulletin.pending, (state) => {
        state.deleteAutoBulletinRequest.loading = true;
        state.deleteAutoBulletinRequest.error = null;
      })
      .addCase(deleteAutoBulletin.rejected, (state, action) => {
        state.deleteAutoBulletinRequest.loading = false;
        state.deleteAutoBulletinRequest.error = action.payload;
      })
      .addCase(toggleActivationAutoBulletin.fulfilled, (state, action) => {
        if (action.payload?.singleAutoBulletin) {
          state.currentBulletin.active = !state.currentBulletin.active;
        } else {
          state.autoBulletins.data = state.autoBulletins.data.map((item) => {
            if (item.id === action.payload?.id)
              return {
                ...item,
                active: !item.active,
              };

            return item;
          });
        }
        state.toggleActivationAutoBulletinRequest.loading = false;
        state.toggleActivationAutoBulletinRequest.error = null;
      })
      .addCase(toggleActivationAutoBulletin.pending, (state) => {
        state.toggleActivationAutoBulletinRequest.loading = true;
        state.toggleActivationAutoBulletinRequest.error = null;
      })
      .addCase(toggleActivationAutoBulletin.rejected, (state, action) => {
        state.toggleActivationAutoBulletinRequest.loading = false;
        state.toggleActivationAutoBulletinRequest.error = action.payload;
      })
      .addCase(shareHashAutoBulletin.fulfilled, (state, action) => {
        state.autoBulletins.data = state.autoBulletins.data.map((item) => {
          if (item.id === action.payload?.id)
            return {
              ...item,
              hash: action.payload?.hash,
            };

          return item;
        });
        state.shareHashAutoBulletinRequest.loading = false;
        state.shareHashAutoBulletinRequest.error = null;
      })
      .addCase(shareHashAutoBulletin.pending, (state) => {
        state.shareHashAutoBulletinRequest.loading = true;
        state.shareHashAutoBulletinRequest.error = null;
      })
      .addCase(shareHashAutoBulletin.rejected, (state, action) => {
        state.shareHashAutoBulletinRequest.loading = false;
        state.shareHashAutoBulletinRequest.error = action.payload;
      })
      .addCase(fetchAutoBulletinByHash.pending, (state) => {
        state.contents.loading = true;
        state.contents.error = null;
      })
      .addCase(fetchAutoBulletinByHash.fulfilled, (state, action) => {
        state.currentAutoBulletin = action.payload;
      })
      .addCase(fetchAutoBulletinByHash.rejected, (state, action) => {
        state.contents.loading = false;
        state.fetchAutoBulletinByHash.error = action.payload;
      })
      .addCase(fetchAutoBulletinArchives.pending, (state) => {
        state.archives.loading = true;
        state.archives.error = null;
      })
      .addCase(fetchAutoBulletinArchives.fulfilled, (state, action) => {
        state.archives.loading = false;
        state.archives.error = null;
        state.archives.data = action.payload;
      })
      .addCase(fetchAutoBulletinArchives.rejected, (state, action) => {
        state.archives.loading = false;
        state.archives.error = action.payload;
      })
      .addCase(fetchAutoBulletinContents.pending, (state) => {
        state.contents.loading = true;
        state.contents.error = null;
      })
      .addCase(fetchAutoBulletinContents.fulfilled, (state, action) => {
        const { platforms } = action.meta.arg;
        const selectedPlatforms =
          platforms || state.currentAutoBulletin.platforms;
        const { allContents, allPinnedContents, summary, summeryContentCount } =
          action.payload;

        state.contents.loading = false;
        state.contents.error = null;
        selectedPlatforms.forEach((platform) => {
          state.contents[platform] = allContents[platform];
          state.pinnedContents[platform] = allPinnedContents[platform];
        });
        state.summary = summary;
        state.summeryContentCount = summeryContentCount;
      })
      .addCase(fetchAutoBulletinContents.rejected, (state, action) => {
        state.contents.loading = false;
        state.contents.error = action.payload;
      })
      .addCase(deleteAutoBulletinContent.pending, (state) => {
        state.deleteAutoBulletinContent.loading = true;
        state.deleteAutoBulletinContent.error = null;
      })
      .addCase(deleteAutoBulletinContent.fulfilled, (state, action) => {
        const { contentIds, platform } = action.meta.arg;
        state.pinnedContents[platform] = state.pinnedContents[platform]?.filter(
          (pinnedId) => !contentIds.includes(pinnedId)
        );
        state.contents[platform].data = state.contents[platform].data.filter(
          (content) => !contentIds.includes(content.id)
        );
        state.deleteAutoBulletinContent.loading = false;
        state.deleteAutoBulletinContent.error = null;
      })
      .addCase(deleteAutoBulletinContent.rejected, (state, action) => {
        state.deleteAutoBulletinContent.loading = false;
        state.deleteAutoBulletinContent.error = action.payload;
      })
      .addCase(pinAutoBulletinContents.pending, (state) => {
        state.pinAutoBulletinContents.loading = true;
        state.pinAutoBulletinContents.error = null;
      })
      .addCase(pinAutoBulletinContents.fulfilled, (state, action) => {
        const { contentIds, platform } = action.meta.arg;
        const orderedContents = [];
        state.contents[platform].data.forEach((content) => {
          if (contentIds.includes(content.id)) {
            orderedContents.push(content);
          }
        });
        state.contents[platform].data.forEach((content) => {
          if (!contentIds.includes(content.id)) {
            orderedContents.push(content);
          }
        });
        state.contents[platform].data = orderedContents;
        state.pinnedContents[platform] = contentIds;
        state.pinAutoBulletinContents.loading = false;
        state.pinAutoBulletinContents.error = null;
      })
      .addCase(pinAutoBulletinContents.rejected, (state, action) => {
        state.pinAutoBulletinContents.loading = false;
        state.pinAutoBulletinContents.error = action.payload;
      })
      .addCase(fetchWebBulletinContents.fulfilled, (state, action) => {
        const { platform, append } = action.meta.arg;
        const { payload } = action;

        if (append) {
          state.contents[platform].data = [
            ...state.contents[platform].data,
            ...payload,
          ];
        } else {
          state.contents[platform].data = payload;
        }
        state.contents.loading = false;
        state.contents.error = null;
      })
      .addCase(fetchWebBulletinContents.rejected, (state, action) => {
        state.contents.loading = false;
        state.contents.error = action.payload;
      })
      .addCase(fetchWebBulletinContents.pending, (state) => {
        state.contents.loading = true;
        state.contents.error = null;
      })
      .addCase(fetchWebBulletinByHash.pending, (state) => {
        state.contents.loading = true;
        state.contents.error = null;
      })
      .addCase(fetchWebBulletinByHash.fulfilled, (state, action) => {
        state.currentAutoBulletin.title = action.payload.title;
        state.currentAutoBulletin.bulletin = {
          createdAt: action.payload.bulletin.createdAt,
        };

        state.currentAutoBulletin.id = action.payload.id;
      })
      .addCase(fetchWebBulletinByHash.rejected, (state, action) => {
        state.contents.loading = false;
        state.fetchAutoBulletinByHash.error = action.payload;
      })
      .addCase(fetchWebBulletinPlatforms.fulfilled, (state, action) => {
        const platforms = action.payload;
        platforms.forEach((p) => {
          state.contents[p.platform].total = p.count;
        });
        state.currentAutoBulletin.platforms = platforms.map(
          (item) => item.platform
        );
      })
      .addCase(uploadBulletinImage.pending, (state) => {
        state.uploadBulletinImage.loading = true;
        state.uploadBulletinImage.error = null;
      })
      .addCase(uploadBulletinImage.fulfilled, (state, action) => {
        state.uploadBulletinImage.data = action.payload.data.url;
        state.uploadBulletinImage.loading = false;
      })
      .addCase(uploadBulletinImage.rejected, (state, action) => {
        state.uploadBulletinImage.loading = false;
        state.uploadBulletinImage.error = action.payload;
      });
  },
});

export const {
  setCurrentBulletinInitialValues,
  toggleCurrentBulletinModal,
  changeCurrentBulletinValue,
  setSelectedSort,
  setSelectedArchive,
  toggleContentDetails,
  setSelectedContent,
  setSelectedContents,
  undoRemovedContent,
  removeContent,
  setAutoBulletinParamValue,
  setSelectedFilterIds,
  setAdvertFilterValues,
  setSelectedContentBasedValues,
  setSelectedFilterValue,
  resetAllFilters,
  toggleAutoBulletinFilterActivateSwitch,
  setSelectedCategories,
  resetFilter,
  setSelectedItems,
  setSelectedSentimentValues,
  setIsWebBulletin,
  setWebBulletinPageNumber,
} = autoBulletinSlice.actions;
